import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { getDefaultTimeZoneOnBrowser } from '../../../../util/dates';
import { propTypes } from '../../../../util/types';
import {
  Button,
  H3,
  FieldCurrencyInput,
  FieldTextInput,
  IconSpinner,
  Icons,
  FieldSelect,
} from '../../../../components';
import CustomRangeInput from '../CustomRangeInput/CustomRangeInput';
import appSettings from '../../../../config/settings';
import css from './EditListingAvailabilityPanel.module.css';
import moment from 'moment-timezone';
import { useDispatch } from 'react-redux';
import {
  requestAddAvailabilityException,
  requestDeleteAvailabilityException,
} from '../../EditListingPage.duck';
import { isArrayLength } from '../../../../util/genericHelpers';
import {
  convertMoneyToNumber,
  convertUnitToSubUnit,
  formatMoney,
  unitDivisor,
} from '../../../../util/currency';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import * as validators from '../../../../util/validators';
import { useIntl } from 'react-intl';
import { Form } from 'react-final-form';
import {
  getPricingSettings,
  hasOverlappingSeasonalRate,
  normalizePercent,
  normalizeSeasonalRates,
} from '../../../../util/seasonalPricing';
import useListingAutosave from '../useListingAutosave';
import ListingAutosaveStatus from '../ListingAutosaveStatus';

const defaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

const { Money } = sdkTypes;

const getPriceValidators = (listingMinimumPriceSubUnits, marketplaceCurrency, intl) => {
  const priceRequiredMsgId = { id: 'EditListingPricingForm.priceRequired' };
  const priceRequiredMsg = intl.formatMessage(priceRequiredMsgId);
  const priceRequired = validators.required(priceRequiredMsg);

  const minPriceRaw = new Money(listingMinimumPriceSubUnits, marketplaceCurrency);
  const minPrice = formatMoney(intl, minPriceRaw);
  const priceTooLowMsgId = { id: 'EditListingPricingForm.priceTooLow' };
  const priceTooLowMsg = intl.formatMessage(priceTooLowMsgId, { minPrice });
  const minPriceRequired = validators.moneySubUnitAmountZeroOrAtLeast(
    priceTooLowMsg,
    listingMinimumPriceSubUnits
  );

  return listingMinimumPriceSubUnits
    ? validators.composeValidators(priceRequired, minPriceRequired)
    : priceRequired;
};

const discountValidator = errorMessage => value => {
  if (value === '' || value == null) {
    return undefined;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0 && numberValue <= 99
    ? undefined
    : errorMessage;
};

const moneyFromAmount = (amount, currency) => new Money(Math.round(amount || 0), currency);

const formatAverageNightlyRate = (intl, amount, currency, discountPercent) => {
  const discount = normalizePercent(discountPercent);
  const discountedAmount = Math.round((amount || 0) - ((amount || 0) * discount) / 100);
  return formatMoney(intl, moneyFromAmount(discountedAmount, currency));
};

const formatRateDateRange = rate =>
  `${moment(rate.startDate).format('MMM D, YYYY')} - ${moment(rate.endDate).format('MMM D, YYYY')}`;

const countBlockedDays = exceptions => {
  if (!isArrayLength(exceptions)) {
    return 0;
  }

  return exceptions.reduce((sum, exception) => {
    const start = moment(exception?.attributes?.start);
    const end = moment(exception?.attributes?.end);
    const diff = end.isValid() && start.isValid() ? end.diff(start, 'days') : 0;
    return sum + Math.max(diff, 1);
  }, 0);
};

const parseSeasonalAmount = (amount, currency) => {
  try {
    const parsed = convertUnitToSubUnit(amount, unitDivisor(currency));
    return parsed > 0 ? parsed : null;
  } catch (e) {
    return null;
  }
};

const EditListingAvailabilityPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    onNextTab,
    onSubmit,
    onAutosave,
    allExceptions,
    addExceptionInProgress,
    isNewListingFlow,
    availabilityTab,
    viewport,
    unitType,
    marketplaceCurrency,
    listingMinimumPriceSubUnits,
    updateInProgress,
  } = props;

  const dispatch = useDispatch();
  const intl = useIntl();

  const [showLoader, setLoader] = useState(false);
  const [cacheLoader, setCacheLoader] = useState(false);
  const [cacheLoaderReady, setCacheLoaderReady] = useState(false);
  const [deleteExceptionId, setDeleteExceptionId] = useState(null);
  const [clearDates, setClearDates] = useState(false);
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState('startDate');
  const [markUnavailable, onSetMarkUnavailable] = useState(false);

  const { publicData, createdAt, price } = listing?.attributes || {};
  const settingsCurrency = price?.currency || marketplaceCurrency;
  const pricingSettings = getPricingSettings(publicData, settingsCurrency);
  const { instantBooking } = publicData || {};
  const [seasonalRates, setSeasonalRates] = useState(pricingSettings.seasonalRates);
  const [seasonalFormOpen, setSeasonalFormOpen] = useState(false);
  const [editingSeasonalRateId, setEditingSeasonalRateId] = useState(null);
  const [seasonalStartDate, setSeasonalStartDate] = useState(null);
  const [seasonalEndDate, setSeasonalEndDate] = useState(null);
  const [seasonalFocusedInput, setSeasonalFocusedInput] = useState('startDate');
  const [seasonalLabel, setSeasonalLabel] = useState('');
  const [seasonalAmount, setSeasonalAmount] = useState('');
  const [seasonalRateError, setSeasonalRateError] = useState(null);

  const classes = classNames(
    rootClassName || css.root,
    className,
    availabilityTab ? css.availabilityTab : null
  );

  const priceValidators = getPriceValidators(
    listingMinimumPriceSubUnits,
    marketplaceCurrency,
    intl
  );
  const discountError = intl.formatMessage({
    id: 'EditListingAvailabilityPanel.discountValidation',
  });
  const validateDiscount = discountValidator(discountError);

  const resetSeasonalForm = () => {
    setSeasonalFormOpen(false);
    setEditingSeasonalRateId(null);
    setSeasonalStartDate(null);
    setSeasonalEndDate(null);
    setSeasonalFocusedInput('startDate');
    setSeasonalLabel('');
    setSeasonalAmount('');
    setSeasonalRateError(null);
  };

  const openSeasonalForm = rate => {
    setSeasonalFormOpen(true);
    setEditingSeasonalRateId(rate?.id || null);
    setSeasonalStartDate(rate?.startDate ? moment(rate.startDate) : null);
    setSeasonalEndDate(rate?.endDate ? moment(rate.endDate) : null);
    setSeasonalFocusedInput('startDate');
    setSeasonalLabel(rate?.label || '');
    setSeasonalAmount(
      rate?.amount && rate?.currency
        ? String(convertMoneyToNumber(new Money(rate.amount, rate.currency)))
        : ''
    );
    setSeasonalRateError(null);
  };

  const saveSeasonalRate = (currency, form) => {
    const amount = parseSeasonalAmount(seasonalAmount, currency);
    const startDateString = seasonalStartDate ? seasonalStartDate.format('YYYY-MM-DD') : null;
    const endDateString = seasonalEndDate ? seasonalEndDate.format('YYYY-MM-DD') : null;

    if (!startDateString || !endDateString || moment(endDateString).isBefore(startDateString)) {
      setSeasonalRateError(
        intl.formatMessage({ id: 'EditListingAvailabilityPanel.seasonalDateRequired' })
      );
      return;
    }

    if (!amount) {
      setSeasonalRateError(
        intl.formatMessage({ id: 'EditListingAvailabilityPanel.seasonalRateRequired' })
      );
      return;
    }

    const nextRate = {
      id: editingSeasonalRateId || `seasonal-${Date.now()}`,
      label: seasonalLabel.trim(),
      startDate: startDateString,
      endDate: endDateString,
      amount,
      currency,
    };

    if (hasOverlappingSeasonalRate(seasonalRates, nextRate, editingSeasonalRateId)) {
      setSeasonalRateError(
        intl.formatMessage({ id: 'EditListingAvailabilityPanel.seasonalRateOverlap' })
      );
      return;
    }

    const withoutEditedRate = seasonalRates.filter(rate => rate.id !== editingSeasonalRateId);
    const nextRates = [...withoutEditedRate, nextRate].sort((a, b) =>
      a.startDate.localeCompare(b.startDate)
    );
    setSeasonalRates(nextRates);
    form?.change('seasonalRates', nextRates);
    resetSeasonalForm();
  };

  const defaultAvailabilityPlan = {
    type: 'availability-plan/time',
    timezone: defaultTimeZone(),
    entries: [
      { dayOfWeek: 'mon', startTime: '00:00', endTime: '00:00', seats: 1 },
      { dayOfWeek: 'tue', startTime: '00:00', endTime: '00:00', seats: 1 },
      { dayOfWeek: 'wed', startTime: '00:00', endTime: '00:00', seats: 1 },
      { dayOfWeek: 'thu', startTime: '00:00', endTime: '00:00', seats: 1 },
      { dayOfWeek: 'fri', startTime: '00:00', endTime: '00:00', seats: 1 },
      { dayOfWeek: 'sat', startTime: '00:00', endTime: '00:00', seats: 1 },
      { dayOfWeek: 'sun', startTime: '00:00', endTime: '00:00', seats: 1 },
    ],
  };

  const availabilityPlan = defaultAvailabilityPlan;

  const buildUpdateValues = values => {
    const priceCurrency = values?.price?.currency || price?.currency || marketplaceCurrency;
    const savedSeasonalRates = normalizeSeasonalRates(
      values?.seasonalRates || seasonalRates,
      priceCurrency
    );
    return {
      id: listing?.id,
      availabilityPlan,
      price: values?.price || price,
      publicData: {
        ...publicData,
        instantBooking: values?.instantBooking,
        weeklyDiscountPercent: normalizePercent(values?.weeklyDiscountPercent),
        monthlyDiscountPercent: normalizePercent(values?.monthlyDiscountPercent),
        seasonalRates: savedSeasonalRates,
      },
    };
  };

  const handleSubmit = values => {
    setCacheLoader(true);
    return onSubmit(buildUpdateValues(values))
      .then(() => {
        setCacheLoader(false);
        setCacheLoaderReady(true);
        if (isNewListingFlow) {
          onNextTab();
        }
      })
      .catch(e => {
        setCacheLoader(false);
        setCacheLoaderReady(false);
        // Don't close modal if there was an error
      });
  };

  useEffect(() => {
    if (!focusedInput) {
      setFocusedInput('startDate');
    }
  }, [focusedInput]);

  useEffect(() => {
    setSeasonalRates(getPricingSettings(publicData, settingsCurrency).seasonalRates);
  }, [listing?.id?.uuid, settingsCurrency]);

  const isDateBlocked = day => {
    // Block dates before today
    if (day.isBefore(moment().startOf('day'))) {
      return true;
    }

    if (isArrayLength(allExceptions)) {
      const dayMoment = moment(day);

      // Check if the day is within any of the exception date ranges
      const isBlockedByExceptions = allExceptions?.some(exception => {
        const exceptionStart = moment(exception?.attributes?.start);
        const exceptionEnd = moment(exception?.attributes?.end);
        return dayMoment.isBetween(exceptionStart, exceptionEnd, 'day', '[]');
      });

      return isBlockedByExceptions;
    }
  };

  const renderAvailabilityContent = (
    <>
      <div className={css.sidebarBlock}>
        <div className={css.availableDays}>
          <span
            className={css.markAvailability}
            onClick={() => onSetMarkUnavailable(!markUnavailable)}
          >
            Mark as unavailable
            {markUnavailable ? <Icons name="redCircle" /> : <Icons name="blankCircle" />}
          </span>
          {markUnavailable ? (
            <div className={css.availabilityExceptionButtons}>
              <Button
                type="button"
                disabled={!endDate}
                inProgress={addExceptionInProgress}
                onClick={() => {
                  const isSingleDay = moment(endDate).diff(moment(startDate), 'days') === 0;

                  // Assuming startDate and endDate are in the same timezone
                  const startOfDay = moment(startDate)
                    .tz(defaultTimeZone())
                    .startOf('day')
                    .toDate();

                  const endOfDay = moment(endDate)
                    .tz(defaultTimeZone())
                    .toDate();
                  if (isSingleDay) {
                    endOfDay.setHours(23, 55, 0, 0);
                  } else {
                    endOfDay.setHours(0, 0, 0, 0);
                  }
                  dispatch(
                    requestAddAvailabilityException({
                      listingId: listing?.id,
                      start: startOfDay,
                      end: endOfDay,
                      seats: 0,
                    })
                  )
                    .then(() => {})
                    .catch(() => {})
                    .finally(() => {
                      setStartDate(null);
                      setEndDate(null);
                    });
                }}
              >
                Block Dates
              </Button>
              <Button
                disabled={!startDate}
                type="button"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
              >
                Clear
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  // Check if listing is older than the current date
  const isOldListing = createdAt ? new Date(createdAt) < new Date() : false;

  // Determine instantBooking behavior
  const computedInstantBooking = isNewListingFlow
    ? 'true'
    : isOldListing
    ? instantBooking ?? 'false' // Keep false/null values for old listings
    : instantBooking ?? 'true'; // Default new listings to true

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        price,
        instantBooking: computedInstantBooking,
        weeklyDiscountPercent: pricingSettings.weeklyDiscountPercent,
        monthlyDiscountPercent: pricingSettings.monthlyDiscountPercent,
      }}
      render={({ form, handleSubmit: finalFormHandleSubmit, values, dirty, invalid }) => {
        const autosave = useListingAutosave({
          values,
          dirty,
          valid: !invalid,
          onSave: autosaveValues => onAutosave(buildUpdateValues(autosaveValues)),
          enabled: !!onAutosave,
        });
        const currency = values?.price?.currency || price?.currency || marketplaceCurrency;
        const nightlyAmount = values?.price?.amount ?? price?.amount ?? 0;
        const weeklyDiscount = normalizePercent(values?.weeklyDiscountPercent);
        const monthlyDiscount = normalizePercent(values?.monthlyDiscountPercent);
        const baseRateSummary = nightlyAmount
          ? `${formatMoney(intl, moneyFromAmount(nightlyAmount, currency))}/night`
          : '-';
        const selectedSeasonalDates =
          seasonalStartDate && seasonalEndDate
            ? `${seasonalStartDate.format('MMM D, YYYY')} - ${seasonalEndDate.format(
                'MMM D, YYYY'
              )}`
            : intl.formatMessage({ id: 'EditListingAvailabilityPanel.selectSeasonalDates' });

        return (
          <form onSubmit={autosave.handleManualSubmit(finalFormHandleSubmit)}>
            <main className={classes}>
              <div className={css.priceAvailabilityHeader}>
                <H3 as="h2">
                  {intl.formatMessage({ id: 'EditListingAvailabilityPanel.priceAvailability' })}
                </H3>
                <p className={css.priceDescription}>
                  {intl.formatMessage({
                    id: 'EditListingAvailabilityPanel.priceAvailabilityDescription',
                  })}
                </p>
              </div>

              <div className={css.priceAvailabilityLayout}>
                <div className={css.priceAvailabilityForm}>
                  <section className={css.settingsSection}>
                    <h3>{intl.formatMessage({ id: 'EditListingAvailabilityPanel.rate' })}</h3>
                    <div className={css.rateCard}>
                      <FieldCurrencyInput
                        id="price"
                        name="price"
                        className={css.priceInput}
                        validate={priceValidators}
                        label={intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.nightlyRate',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'EditListingPricingForm.priceInputPlaceholder',
                        })}
                        currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                      />
                    </div>

                    <div className={css.discountCard}>
                      <div className={css.discountCopy}>
                        <label htmlFor="weeklyDiscountPercent">
                          {intl.formatMessage({
                            id: 'EditListingAvailabilityPanel.weeklyDiscount',
                          })}
                        </label>
                        <span>
                          {intl.formatMessage({ id: 'EditListingAvailabilityPanel.weeklyHelp' })}
                        </span>
                      </div>
                      <div className={css.discountControls}>
                        <span className={css.discountPreview}>
                          {intl.formatMessage(
                            { id: 'EditListingAvailabilityPanel.weeklyAverage' },
                            {
                              average: formatAverageNightlyRate(
                                intl,
                                nightlyAmount,
                                currency,
                                weeklyDiscount
                              ),
                            }
                          )}
                        </span>
                        <div className={css.percentInputWrapper}>
                          <FieldTextInput
                            id="weeklyDiscountPercent"
                            name="weeklyDiscountPercent"
                            type="number"
                            min="0"
                            max="99"
                            step="1"
                            validate={validateDiscount}
                            inputRootClass={css.percentInput}
                          />
                          <span>%</span>
                        </div>
                      </div>
                    </div>

                    <div className={css.discountCard}>
                      <div className={css.discountCopy}>
                        <label htmlFor="monthlyDiscountPercent">
                          {intl.formatMessage({
                            id: 'EditListingAvailabilityPanel.monthlyDiscount',
                          })}
                        </label>
                        <span>
                          {intl.formatMessage({ id: 'EditListingAvailabilityPanel.monthlyHelp' })}
                        </span>
                      </div>
                      <div className={css.discountControls}>
                        <span className={css.discountPreview}>
                          {intl.formatMessage(
                            { id: 'EditListingAvailabilityPanel.monthlyAverage' },
                            {
                              average: formatAverageNightlyRate(
                                intl,
                                nightlyAmount,
                                currency,
                                monthlyDiscount
                              ),
                            }
                          )}
                        </span>
                        <div className={css.percentInputWrapper}>
                          <FieldTextInput
                            id="monthlyDiscountPercent"
                            name="monthlyDiscountPercent"
                            type="number"
                            min="0"
                            max="99"
                            step="1"
                            validate={validateDiscount}
                            inputRootClass={css.percentInput}
                          />
                          <span>%</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className={css.settingsSection}>
                    <div className={css.sectionTitleRow}>
                      <div>
                        <h3>
                          {intl.formatMessage({ id: 'EditListingAvailabilityPanel.seasonalRate' })}
                        </h3>
                        <p>
                          {intl.formatMessage({
                            id: 'EditListingAvailabilityPanel.seasonalRateDescription',
                          })}
                        </p>
                      </div>
                    </div>

                    {seasonalFormOpen ? (
                      <div className={css.seasonalFormCard}>
                        <div className={css.selectedDatesRow}>
                          <div>
                            <span>
                              {intl.formatMessage({
                                id: 'EditListingAvailabilityPanel.selectedDates',
                              })}
                            </span>
                            <strong>{selectedSeasonalDates}</strong>
                          </div>
                          <button
                            type="button"
                            className={css.linkButton}
                            onClick={() => {
                              setSeasonalStartDate(null);
                              setSeasonalEndDate(null);
                              setSeasonalFocusedInput('startDate');
                            }}
                          >
                            {intl.formatMessage({ id: 'EditListingAvailabilityPanel.clearDates' })}
                          </button>
                        </div>

                        {seasonalStartDate && seasonalEndDate && !seasonalFocusedInput ? (
                          <button
                            type="button"
                            className={css.changeSeasonalDatesButton}
                            onClick={() => setSeasonalFocusedInput('startDate')}
                          >
                            {intl.formatMessage({
                              id: 'EditListingAvailabilityPanel.changeSeasonalDates',
                            })}
                          </button>
                        ) : (
                          <CustomRangeInput
                            onDatesChange={({ startDate, endDate }) => {
                              if (startDate && endDate) {
                                setSeasonalFocusedInput(null);
                              }
                            }}
                            numberOfMonths={2}
                            startDateId="seasonal_start_date"
                            endDateId="seasonal_end_date"
                            keepOpenOnDateSelect={false}
                            startDate={seasonalStartDate}
                            endDate={seasonalEndDate}
                            isOutsideRange={day => day.isBefore(moment().startOf('day'))}
                            setStartDate={d => {
                              setSeasonalStartDate(d);
                              if (seasonalEndDate && d && seasonalEndDate.isBefore(d)) {
                                setSeasonalEndDate(null);
                              }
                            }}
                            setEndDate={setSeasonalEndDate}
                            focusedInput={seasonalFocusedInput}
                            setFocusedInput={setSeasonalFocusedInput}
                            className={css.seasonalCalendar}
                            viewport={viewport}
                          />
                        )}

                        <div className={css.seasonalFields}>
                          <label>
                            <span>
                              {intl.formatMessage({
                                id: 'EditListingAvailabilityPanel.seasonName',
                              })}
                            </span>
                            <input
                              type="text"
                              value={seasonalLabel}
                              onChange={e => setSeasonalLabel(e.target.value)}
                              placeholder={intl.formatMessage({
                                id: 'EditListingAvailabilityPanel.seasonNamePlaceholder',
                              })}
                            />
                          </label>
                          <label>
                            <span>
                              {intl.formatMessage({
                                id: 'EditListingAvailabilityPanel.seasonalNightlyRate',
                              })}
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={seasonalAmount}
                              onChange={e => setSeasonalAmount(e.target.value)}
                              placeholder="95.00"
                            />
                          </label>
                        </div>

                        {seasonalRateError ? (
                          <p className={css.seasonalError}>{seasonalRateError}</p>
                        ) : null}

                        <div className={css.seasonalActions}>
                          <Button type="button" onClick={() => saveSeasonalRate(currency, form)}>
                            {intl.formatMessage({
                              id: 'EditListingAvailabilityPanel.saveSeasonalRate',
                            })}
                          </Button>
                          <button
                            type="button"
                            className={css.cancelButton}
                            onClick={resetSeasonalForm}
                          >
                            {intl.formatMessage({ id: 'EditListingAvailabilityPanel.cancel' })}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={css.addSeasonalRateButton}
                        onClick={() => openSeasonalForm(null)}
                      >
                        <span>+</span>
                        {intl.formatMessage({ id: 'EditListingAvailabilityPanel.addSeasonalRate' })}
                      </button>
                    )}

                    {seasonalRates.length > 0 ? (
                      <div className={css.savedSeasonalRates}>
                        {seasonalRates.map(rate => (
                          <div className={css.savedSeasonalRate} key={rate.id}>
                            <div>
                              <strong>
                                {rate.label ||
                                  intl.formatMessage({
                                    id: 'EditListingAvailabilityPanel.unnamedSeason',
                                  })}
                              </strong>
                              <span>{formatRateDateRange(rate)}</span>
                              <span>
                                {formatMoney(intl, moneyFromAmount(rate.amount, rate.currency))}
                                /night
                              </span>
                            </div>
                            <div className={css.rateCardActions}>
                              <button type="button" onClick={() => openSeasonalForm(rate)}>
                                {intl.formatMessage({ id: 'EditListingAvailabilityPanel.edit' })}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const nextRates = seasonalRates.filter(
                                    savedRate => savedRate.id !== rate.id
                                  );
                                  setSeasonalRates(nextRates);
                                  form.change('seasonalRates', nextRates);
                                  if (editingSeasonalRateId === rate.id) {
                                    resetSeasonalForm();
                                  }
                                }}
                              >
                                {intl.formatMessage({ id: 'EditListingAvailabilityPanel.remove' })}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>

                  <section className={css.settingsSection}>
                    <FieldSelect
                      id="instantBooking"
                      className={css.quantityField}
                      name="instantBooking"
                      label={intl.formatMessage({
                        id: 'EditListingAvailabilityPanel.instantBooking',
                      })}
                    >
                      <option disabled value="">
                        {intl.formatMessage({ id: 'EditListingAvailabilityPanel.selectOption' })}
                      </option>
                      <option value="true">
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.instantBookingYes',
                        })}
                      </option>
                      <option value="false">
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.instantBookingNo',
                        })}
                      </option>
                    </FieldSelect>

                    {values?.instantBooking == 'true' && (
                      <p className={css.instantBookingInfo}>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.instantBookingInfo',
                        })}
                      </p>
                    )}
                  </section>

                  <section className={css.settingsSection}>
                    <div className={css.pricingHeading}>
                      <H3 as="h2" className={css.availabilityHeading}>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.availabilityHeading',
                        })}
                      </H3>
                      <p>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.availabilityDescription',
                        })}
                      </p>
                    </div>
                    <div className={css.customCalendarSec}>
                      <CustomRangeInput
                        isAvailablityCalendar={true}
                        onDatesChange={() => setClearDates(false)}
                        numberOfMonths={2}
                        startDateId="availability_start_date"
                        endDateId="availability_end_date"
                        clearDates={clearDates}
                        allExceptions={allExceptions}
                        startDate={startDate}
                        endDate={endDate}
                        isOutsideRange={isDateBlocked}
                        setStartDate={d => {
                          setStartDate(d);
                          setEndDate(d);
                        }}
                        setEndDate={d => {
                          onSetMarkUnavailable(true);
                          setEndDate(d);
                        }}
                        focusedInput={focusedInput}
                        setFocusedInput={setFocusedInput}
                        className={css.customCalendar}
                        viewport={viewport}
                      />
                      <aside className={css.sideBar}>{renderAvailabilityContent}</aside>
                    </div>
                    <div className={css.allExceptions}>
                      <h4>
                        {intl.formatMessage({ id: 'EditListingAvailabilityPanel.blockedDates' })}
                      </h4>
                      {isArrayLength(allExceptions)
                        ? allExceptions?.map((a, i) => (
                            <div className={css.exceptionDates} key={a?.id?.uuid || i}>
                              <span className={css.startDate}>
                                {moment(a?.attributes?.start).format('MM/DD/YYYY')}
                              </span>
                              <span className={css.endDate}>
                                {moment(a?.attributes?.end).format('MM/DD/YYYY')}
                              </span>
                              <span
                                className={css.remove}
                                onClick={() => {
                                  setLoader(true);
                                  setDeleteExceptionId(a?.id?.uuid);
                                  dispatch(
                                    requestDeleteAvailabilityException({
                                      id: a?.id,
                                    })
                                  ).then(() => {
                                    setLoader(false);
                                  });
                                }}
                              >
                                {showLoader && deleteExceptionId === a?.id?.uuid ? (
                                  <IconSpinner />
                                ) : (
                                  'X'
                                )}
                              </span>
                            </div>
                          ))
                        : null}
                    </div>
                  </section>
                </div>

                <aside className={css.summaryPanel}>
                  <h3>
                    {intl.formatMessage({ id: 'EditListingAvailabilityPanel.priceSettings' })}
                  </h3>
                  <dl>
                    <div>
                      <dt>
                        {intl.formatMessage({ id: 'EditListingAvailabilityPanel.baseRateSummary' })}
                      </dt>
                      <dd>{baseRateSummary}</dd>
                    </div>
                    <div>
                      <dt>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.weeklyDiscount',
                        })}
                      </dt>
                      <dd>{weeklyDiscount}%</dd>
                    </div>
                    <div>
                      <dt>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.monthlyDiscount',
                        })}
                      </dt>
                      <dd>{monthlyDiscount}%</dd>
                    </div>
                    <div>
                      <dt>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.seasonalRatesSummary',
                        })}
                      </dt>
                      <dd>
                        {intl.formatMessage(
                          { id: 'EditListingAvailabilityPanel.seasonalRatesCount' },
                          { count: seasonalRates.length }
                        )}
                      </dd>
                    </div>
                  </dl>
                  <h3>
                    {intl.formatMessage({
                      id: 'EditListingAvailabilityPanel.availabilitySettings',
                    })}
                  </h3>
                  <dl>
                    <div>
                      <dt>
                        {intl.formatMessage({
                          id: 'EditListingAvailabilityPanel.blockedDatesSummary',
                        })}
                      </dt>
                      <dd>
                        {intl.formatMessage(
                          { id: 'EditListingAvailabilityPanel.blockedDatesCount' },
                          { count: countBlockedDays(allExceptions) }
                        )}
                      </dd>
                    </div>
                  </dl>
                </aside>
              </div>

              <div className={css.actionBtnSec}>
                {onAutosave ? (
                  <ListingAutosaveStatus status={autosave.status} onRetry={autosave.retry} />
                ) : null}
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={cacheLoader || updateInProgress || autosave.isSaving}
                  disabled={cacheLoader || updateInProgress || autosave.isSaving}
                  ready={cacheLoaderReady}
                >
                  {intl.formatMessage({
                    id: isNewListingFlow
                      ? 'EditListingAvailabilityPanel.nextPhotos'
                      : 'EditListingAvailabilityPanel.savePrice',
                  })}
                </Button>
              </div>
            </main>
          </form>
        );
      }}
    />
  );
};

export default EditListingAvailabilityPanel;
